import { Container, Typography, Divider, Box, Stack } from "@mui/material";
import Navbar from "../Navbar/Navbar";
import Footer from "../Footer/Footer";
import { useParams } from "react-router-dom";

const ReturnRefundPolicy = () => {
  const { id } = useParams();
  const showLayout = id === "1";

  const sections = [
    {
      number: "01",
      title: "Introduction",
      content:
        "Hindustan Wardrobe is a platform designed to facilitate seamless dress rentals between renters and users. This policy outlines the procedures for returns, refunds, and dispute resolutions to ensure transparency and fairness for all parties involved.",
    },
    {
      number: "02",
      title: "Return Process",
      points: [
        {
          label: "Self-Delivery",
          text: "All deliveries and returns are coordinated directly between the renter and the user.",
        },
        {
          label: "Return Confirmation",
          text: "Upon return, the user must provide a 4-digit OTP to the renter to confirm the return.",
        },
      ],
    },
    {
      number: "03",
      title: "Late Returns",
      points: [
        {
          label: "Responsibility",
          text: "In cases of late returns, the renter is responsible for coordinating with the user to manage any delays.",
        },
        {
          label: "Late Fee Claims",
          text: "If a dress is returned late, the renter must raise a request within 24 hours of the scheduled return time. Our support team will verify the claim and, if valid, deduct the appropriate amount from the user's security deposit.",
        },
      ],
    },
    {
      number: "04",
      title: "Damaged Items",
      points: [
        {
          label: "Reporting Damages",
          text: "If a dress is returned damaged, the renter must report the issue within 24 hours of return.",
        },
        {
          label: "Verification and Deductions",
          text: "Our support team will assess the extent of the damage. If confirmed, the cost of repairs or replacement will be deducted from the user's security deposit.",
        },
        {
          label: "Exceeding Deposit Amount",
          text: "If the damage cost exceeds the deposit, the renter is responsible for recovering the remaining amount directly from the user. Hindustan Wardrobe will not mediate beyond the collected deposit.",
        },
      ],
    },
    {
      number: "05",
      title: "Security Deposit",
      points: [
        {
          label: "Collection",
          text: "Security deposits are collected at the time of booking.",
        },
        {
          label: "Refunds",
          text: "Deposits are refunded to the user's Hindustan Wardrobe wallet within 48 hours of return in satisfactory condition.",
        },
        {
          label: "Deductions",
          text: "Verified late fees or damage costs will be deducted from the deposit before refunding.",
        },
      ],
    },
    {
      number: "06",
      title: "Platform Role",
      points: [
        {
          label: "Facilitation",
          text: "We provide a structured platform for renters to list dresses and users to rent them.",
        },
        {
          label: "Support",
          text: "We facilitate bookings and resolve disputes within the scope of the security deposit but do not handle logistics.",
        },
        {
          label: "Limitations",
          text: "Our responsibility is limited to the security deposit. Claims beyond this must be resolved between renter and user.",
        },
      ],
    },
    {
      number: "07",
      title: "Dispute Resolution",
      points: [
        {
          label: "Timely Reporting",
          text: "Disputes must be reported within 24 hours of the scheduled return time.",
        },
        {
          label: "Evidence Submission",
          text: "Both parties may be required to submit evidence such as photos, chat logs, or other relevant information to support their claims.",
        },
        {
          label: "Final Decision",
          text: "Hindustan Wardrobe will assess the available evidence and make a final decision regarding any deductions.",
        },
      ],
    },
  ];

  return (
    <>
      {showLayout && <Navbar />}

      <Box
        sx={{
          background: "#F7F4EF",
          minHeight: "100vh",
          color: "#171717",
        }}
      >
        <Box
          sx={{
            borderBottom: "1px solid #DDD7CE",
            py: { xs: 7, md: 10 },
            position: "relative",
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              position: "absolute",
              width: 320,
              height: 320,
              border: "1px solid rgba(151,119,67,0.12)",
              borderRadius: "50%",
              right: { xs: -170, md: -100 },
              top: -160,
            }}
          />

          <Box
            sx={{
              position: "absolute",
              width: 180,
              height: 180,
              border: "1px solid rgba(151,119,67,0.1)",
              borderRadius: "50%",
              left: { xs: -100, md: -50 },
              bottom: -100,
            }}
          />

          <Container
            maxWidth="lg"
            sx={{
              position: "relative",
              zIndex: 1,
            }}
          >
            <Typography
              sx={{
                fontSize: 11,
                letterSpacing: "0.3em",
                textTransform: "uppercase",
                color: "#977743",
                fontWeight: 600,
                mb: 2.5,
              }}
            >
              Hindustan Wardrobe · Customer Care
            </Typography>

            <Typography
              component="h1"
              sx={{
                fontFamily: "Georgia, serif",
                fontWeight: 400,
                fontSize: {
                  xs: 46,
                  sm: 60,
                  md: 76,
                },
                lineHeight: 0.98,
                letterSpacing: "-0.03em",
                maxWidth: 800,
                mb: 3,
              }}
            >
              Return &
              <br />
              <Box component="span" sx={{ fontStyle: "italic" }}>
                Refund Policy
              </Box>
            </Typography>

            <Typography
              sx={{
                maxWidth: 620,
                color: "#68635C",
                fontSize: { xs: 14, md: 16 },
                lineHeight: 1.8,
              }}
            >
              Our return, refund and dispute guidelines are designed to keep
              the rental experience transparent, structured and fair for both
              renters and users.
            </Typography>
          </Container>
        </Box>

        <Container
          maxWidth="md"
          sx={{
            py: { xs: 6, md: 9 },
          }}
        >
          <Box
            sx={{
              background: "#171717",
              color: "#fff",
              p: { xs: 3.5, md: 5 },
              mb: { xs: 6, md: 8 },
            }}
          >
            <Typography
              sx={{
                fontSize: 10,
                letterSpacing: "0.25em",
                textTransform: "uppercase",
                color: "#C4A36A",
                mb: 2,
              }}
            >
              Please Read Carefully
            </Typography>

            <Typography
              sx={{
                fontFamily: "Georgia, serif",
                fontSize: { xs: 24, md: 30 },
                lineHeight: 1.35,
              }}
            >
              Understanding the return process helps ensure a smooth rental
              experience for everyone.
            </Typography>
          </Box>

          <Stack spacing={0}>
            <Box
              sx={{
                pb: 5,
                mb: 5,
                borderBottom: "1px solid #DDD7CE",
              }}
            >
              <Stack
                direction="row"
                spacing={3}
                alignItems="flex-start"
              >
                <Typography
                  sx={{
                    fontSize: 12,
                    letterSpacing: "0.15em",
                    color: "#977743",
                    fontWeight: 600,
                    minWidth: 30,
                  }}
                >
                  01
                </Typography>

                <Box>
                  <Typography
                    component="h2"
                    sx={{
                      fontFamily: "Georgia, serif",
                      fontSize: { xs: 27, md: 32 },
                      fontWeight: 400,
                      mb: 2,
                    }}
                  >
                    Introduction
                  </Typography>

                  <Typography
                    sx={{
                      color: "#625E58",
                      fontSize: 15,
                      lineHeight: 1.9,
                    }}
                  >
                    Hindustan Wardrobe is a platform designed to facilitate
                    seamless dress rentals between renters and users. This
                    policy outlines the procedures for returns, refunds, and
                    dispute resolutions to ensure transparency and fairness for
                    all parties involved.
                  </Typography>
                </Box>
              </Stack>
            </Box>

            {sections.slice(1).map((section) => (
              <Box
                key={section.number}
                sx={{
                  pb: 5,
                  mb: 5,
                  borderBottom: "1px solid #DDD7CE",
                }}
              >
                <Stack
                  direction="row"
                  spacing={{ xs: 2, md: 3 }}
                  alignItems="flex-start"
                >
                  <Typography
                    sx={{
                      fontSize: 12,
                      letterSpacing: "0.15em",
                      color: "#977743",
                      fontWeight: 600,
                      minWidth: 30,
                    }}
                  >
                    {section.number}
                  </Typography>

                  <Box sx={{ flex: 1 }}>
                    <Typography
                      component="h2"
                      sx={{
                        fontFamily: "Georgia, serif",
                        fontSize: { xs: 27, md: 32 },
                        fontWeight: 400,
                        mb: 3,
                      }}
                    >
                      {section.title}
                    </Typography>

                    <Stack spacing={2.5}>
                      {section.points?.map((point) => (
                        <Box key={point.label}>
                          <Typography
                            sx={{
                              fontSize: 13,
                              fontWeight: 700,
                              color: "#252525",
                              mb: 0.5,
                            }}
                          >
                            {point.label}
                          </Typography>

                          <Typography
                            sx={{
                              color: "#625E58",
                              fontSize: 15,
                              lineHeight: 1.85,
                            }}
                          >
                            {point.text}
                          </Typography>
                        </Box>
                      ))}
                    </Stack>
                  </Box>
                </Stack>
              </Box>
            ))}

            <Box
              sx={{
                background: "#EEE9E1",
                p: { xs: 3.5, md: 5 },
                mt: 1,
              }}
            >
              <Typography
                sx={{
                  fontSize: 11,
                  letterSpacing: "0.2em",
                  textTransform: "uppercase",
                  color: "#977743",
                  fontWeight: 600,
                  mb: 2,
                }}
              >
                08 · Contact Us
              </Typography>

              <Typography
                component="h2"
                sx={{
                  fontFamily: "Georgia, serif",
                  fontSize: { xs: 28, md: 34 },
                  fontWeight: 400,
                  mb: 2,
                }}
              >
                Need assistance?
              </Typography>

              <Typography
                sx={{
                  color: "#625E58",
                  fontSize: 15,
                  lineHeight: 1.8,
                  mb: 3,
                }}
              >
                If you have any questions or concerns about this policy, feel
                free to reach out to our support team.
              </Typography>

              <Divider sx={{ borderColor: "#D5CEC3", mb: 3 }} />

              <Stack spacing={1}>
                <Typography
                  sx={{
                    fontSize: 14,
                    color: "#292929",
                  }}
                >
                  <Box component="span" sx={{ fontWeight: 700 }}>
                    Email:
                  </Box>{" "}
                  <Box
                    component="a"
                    href="mailto:info@hindustanwardrobe.com"
                    sx={{
                      color: "#977743",
                      textDecoration: "none",
                      "&:hover": {
                        textDecoration: "underline",
                      },
                    }}
                  >
                    info@hindustanwardrobe.com
                  </Box>
                </Typography>

                <Typography
                  sx={{
                    fontSize: 14,
                    color: "#625E58",
                  }}
                >
                  <Box component="span" sx={{ fontWeight: 700, color: "#292929" }}>
                    Support Hours:
                  </Box>{" "}
                  Monday – Saturday, 10 AM – 5 PM IST
                </Typography>
              </Stack>
            </Box>
          </Stack>
        </Container>
      </Box>

      {showLayout && <Footer />}
    </>
  );
};

export default ReturnRefundPolicy;